import React, { useEffect, useState } from "react";
import { Form, Row, Col, Spin, Tooltip, Space, Table } from "antd";
import { useParams, useSearchParams } from "react-router-dom";
import { getRequest } from "../axios";
import ViewRFQDiameterWise from "../feature/PR/ViewRFQDiameterWise";

const ViewRFQVendor = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const vendorId = searchParams.get("vendor_id");
  const [getResponseData, setGetResponseData] = useState(null);
  const [rfqNumbers, setRfqNumbers] = useState("");
  const [apiLoading, setApiLoading] = useState(false);
  const [isDiameterWise, setIsDiameterWise] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState("₹");
  const [tableData, setTableData] = useState([]);

  const fetchRFQData = async () => {
    setApiLoading(true);
    try {
      const apiResponse = await getRequest(
        `float-rfq/vendor-rfq-detail-view/${id}/?vendor_id=${vendorId}`
      );
      if (apiResponse.data.success) {
        const data = apiResponse.data.data;
        setGetResponseData(data);
        setRfqNumbers(data.rfq_number);
        setIsDiameterWise(data.category.toLowerCase().includes("steel"));
        processTableData(data.city_wise_pr);
      }
    } catch (err) {
      console.error("Error fetching RFQ data:", err);
    } finally {
      setApiLoading(false);
    }
  };

  const processTableData = (cityWisePr) => {
    if (!Array.isArray(cityWisePr)) return;

    const data = [];
    const cityLength = cityWisePr.length;

    cityWisePr.forEach((city, cityIndex) => {
      let cityTotalQuantity = 0;
      let cityTotalQuantityInKg = 0;
      let cityTotalAmount = 0;

      city.site_wise_details.forEach((site, siteIndex) => {
        const seenSubCategories = new Set();
        const materialMap = {};

        site.material_wise_details.forEach((material) => {
          const key = `${material.material_type_name}-${site.site_id}`;
          if (!materialMap[key]) {
            const showSubCategory = !seenSubCategories.has(material.material_type_sub_category);
            seenSubCategories.add(material.material_type_sub_category);
            materialMap[key] = {
              ...material,
              pr_total_quantity: 0,
              pr_total_quantity_by_kg: 0,
              pr_total_amount: 0,
              showSubCategory,
            };
          }
          materialMap[key].pr_total_quantity += material.pr_total_quantity;
          materialMap[key].pr_total_quantity_by_kg += material.pr_total_quantity_by_kg;
          materialMap[key].pr_total_amount += material.pr_total_amount;
        });

        Object.values(materialMap).forEach((materialData, materialDataIndex) => {
          cityTotalQuantity += materialData.pr_total_quantity;
          cityTotalQuantityInKg += materialData.pr_total_quantity_by_kg;
          cityTotalAmount += materialData.pr_total_amount;

          data.push({
            key: `${cityIndex}-${siteIndex}-${materialDataIndex}`,
            srNo: siteIndex === 0 && materialDataIndex === 0 ? cityIndex + 1 : null,
            cityName: siteIndex === 0 && materialDataIndex === 0 ? city.city_name : "",
            siteName: materialDataIndex === 0 ? site.site_name : "",
            subCategory: materialData.showSubCategory ? materialData.material_type_sub_category : "",
            materialName: materialData.material_type_name,
            quantity: materialData.pr_total_quantity,
            uom: materialData.material_type_uom_name === "Cubic Meter" ? "Cu. Mtr." : materialData.material_type_uom_name,
            quantityByKg: materialData.pr_total_quantity_by_kg,
            amount: materialData.pr_total_amount,
          });
        });

        data.push({
          key: `${cityIndex}-${siteIndex}-SiteSubTotal`,
          srNo: null,
          cityName: "",
          siteName: "Sub Total",
          subCategory: "",
          materialName: "",
          quantity: "",
          uom: "",
          quantityByKg: "",
          amount: site.site_total_amount,
        });

        if (cityLength > 1 && siteIndex === city.site_wise_details.length - 1) {
          data.push({
            key: `${cityIndex}-CityTotal`,
            srNo: null,
            cityName: "",
            siteName: "City Total",
            subCategory: "",
            materialName: "",
            quantity: "",
            uom: "",
            quantityByKg: "",
            amount: cityTotalAmount,
          });
        }
      });
    });

    setTableData(data);
  };

  useEffect(() => {
    if (id && vendorId) {
      fetchRFQData();
    }
  }, [id, vendorId]);

  const columns = [
    {
      title: "Sr.",
      dataIndex: "srNo",
      width: 55,
      render: (text) => (text ? <span>{text}</span> : ""),
    },
    {
      title: "City",
      dataIndex: "cityName",
      width: 120,
      render: (text, record, index) => {
        if (index === 0 || record.cityName !== tableData[index - 1]?.cityName) {
          return <span>{text}</span>;
        }
        return "";
      },
    },
    {
      title: "Site",
      dataIndex: "siteName",
      width: 120,
      render: (text, record) => {
        if (record.siteName === "Sub Total") {
          return <span className="subtotal">{text}</span>;
        } else if (record.siteName === "City Total") {
          return <span className="citytotal">{text}</span>;
        }
        return <span>{text}</span>;
      },
    },
    {
      title: "Sub Category",
      dataIndex: "subCategory",
      width: 140,
      render: (text) => <span>{text}</span>,
    },
    {
      title: "Item",
      dataIndex: "materialName",
      width: 120,
      render: (text) => <span>{text}</span>,
    },
    {
      title: (
        <Tooltip color="#094E8F" placement="top" title={<span style={{ color: "#fff" }}>Quantity</span>}>
          Qty.
        </Tooltip>
      ),
      dataIndex: "quantity",
      width: 80,
      align: "center",
      render: (text) =>
        text ? (
          <span>{text.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
        ) : (
          ""
        ),
    },
    {
      title: (
        <Tooltip
          color="#094E8F"
          placement="top"
          title={<span style={{ color: "#fff" }}>Unit Of Measurement</span>}
        >
          UoM.
        </Tooltip>
      ),
      dataIndex: "uom",
      width: 80,
      render: (text) => <span>{text}</span>,
    },
    {
      title: (
        <Tooltip
          color="#094E8F"
          placement="top"
          title={<span style={{ color: "#fff" }}>Quantity in KG</span>}
        >
          Qty. in Kg
        </Tooltip>
      ),
      dataIndex: "quantityByKg",
      width: 110,
      align: "right",
      render: (text) =>
        text ? (
          <span>{text.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
        ) : (
          "-"
        ),
    },
    {
      title: (
        <Tooltip
          color="#094E8F"
          placement="top"
          title={<span style={{ color: "#fff" }}>Estimated Amount</span>}
        >
          Est. Amount
        </Tooltip>
      ),
      dataIndex: "amount",
      width: 140,
      align: "right",
      render: (text, record) => {
        if (!text) return "";
        if (record.siteName === "Sub Total" || record.siteName === "City Total") {
          return (
            <span className={record.siteName === "Sub Total" ? "subtotal" : "citytotal"}>
              {`${currencySymbol} ${text.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
            </span>
          );
        }
        return (
          <span>
            {`${currencySymbol} ${text.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`}
          </span>
        );
      },
    },
  ];

  if (isDiameterWise) {
    return (
      <>
        <div className="ListingTopBar">
          <div className="pagenamewrap">
            <div className="pagename">
              <h3>View RFQ</h3>
            </div>
          </div>
        </div>
        <section className="grid-sec FormSection Container">
          <div className="FormBody selectvendor">
            <Spin spinning={apiLoading}>
              <Form>
                <Row gutter={16}>
                  <Col className="TextFeild gutter-row">
                    <Space>
                      <label className="cp">RFQ Number</label>
                      <strong>{rfqNumbers}</strong>
                    </Space>
                  </Col>
                </Row>
                <ViewRFQDiameterWise getResponseData={getResponseData} />
              </Form>
            </Spin>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <div className="ListingTopBar">
        <div className="pagenamewrap">
          <div className="pagename">
            <h3>View RFQ</h3>
          </div>
        </div>
      </div>
      <section className="grid-sec FormSection Container">
        <div className="FormBody selectvendor">
          <Spin spinning={apiLoading}>
            <Form>
              <Row gutter={16}>
                <Col className="TextFeild gutter-row">
                  <Space>
                    <label className="cp">RFQ Number</label>
                    <strong>{rfqNumbers}</strong>
                  </Space>
                </Col>
              </Row>
              <Table
                columns={columns}
                dataSource={tableData}
                pagination={false}
                rowKey="key"
                className="formtable"
              />
            </Form>
          </Spin>
        </div>
      </section>
    </>
  );
};

export default ViewRFQVendor;